import { useCallback, useEffect, useRef } from 'react';
import { deepEqual } from 'fast-equals';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { useSiteOverrideClient } from '@wix/site-override-client';
import { useDebugLog } from '@wix/cli-debug-log';
import { useExit } from '@wix/cli-ui-kit';
import type { ProjectModelWatcher } from '../../app-manifest/project-model-watcher.js';
import { useProjectModel } from '../../model.js';
import { createSiteOverrides } from '../../site-override/createSiteOverrides.js';
import { useSite } from '../../site/useSite.js';
import { readAppConfig, saveAppConfig } from '../../files/app.config.js';

export function useSiteOverrideUpdater() {
  const { model } = useProjectModel();
  const { site } = useSite();
  const exit = useExit();
  const writeDebugLog = useDebugLog();
  const { createOverride } = useSiteOverrideClient();
  const { appId } = model.config;

  const siteRef = useRef(site);
  const isUpdaterInitialized = useRef(false);

  useEffect(() => {
    siteRef.current = site;

    const updateSiteOverrides = async () => {
      const overrides = createSiteOverrides(model) ?? {};
      const appConfig = await readAppConfig(model.projectFolder, writeDebugLog);
      const siteOverrideId = appConfig?.devSiteOverrideId;

      if (!siteOverrideId) {
        throw new CliError({
          code: CliErrorCode.InvalidSiteOverrideID(),
          cause: null,
        });
      }

      await createOverride({
        appId,
        siteId: site.id,
        overrides,
        siteOverrideId,
      });
    };

    if (isUpdaterInitialized.current) {
      updateSiteOverrides().catch(exit);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);

  return useCallback(
    async ({
      projectModelWatcher,
    }: {
      projectModelWatcher: ProjectModelWatcher;
    }) => {
      const appConfig = await readAppConfig(model.projectFolder, writeDebugLog);
      let overrides = createSiteOverrides(model) ?? {};

      const { id: siteOverrideId } = await createOverride({
        appId,
        siteId: site.id,
        overrides,
        siteOverrideId: appConfig?.devSiteOverrideId,
      });

      projectModelWatcher.onChange(async (newModel, changeType) => {
        if (changeType === 'model-updated') {
          const updatedOverrides = createSiteOverrides(newModel) ?? {};

          if (!deepEqual(overrides, updatedOverrides)) {
            await createOverride({
              appId,
              siteId: siteRef.current.id,
              overrides: updatedOverrides,
              siteOverrideId,
            });

            overrides = updatedOverrides;
          }
        }
      });

      if (siteOverrideId !== appConfig?.devSiteOverrideId) {
        await saveAppConfig(model.projectFolder, {
          devSiteOverrideId: siteOverrideId,
        }).catch((error: unknown) => writeDebugLog(error));
      }

      isUpdaterInitialized.current = true;

      return siteOverrideId;
    },
    [model, writeDebugLog, createOverride, appId, site]
  );
}
